import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../redux";
import { useAuth } from "../hooks/useAuth";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { useCommandPalette } from "../components/CommandPalette";
import { agentActions } from "../redux/reducers/agentSlice";
import { getUsageReport } from "../redux/reducers/usageSlice";
import { getColorForName } from "../utils/colors";
import { makeRequest } from "../utils/request";
import { View, Text, Card, Button, Icon, Tooltip } from "../components/base";
import { useOnboardingState } from "../components/onboarding/useOnboardingState";
import { ONBOARDING_DRAWER_OPEN_EVENT } from "../components/onboarding/onboardingEvents";

// --- Types ---

type AgentEnvironmentSummary = {
  id: string;
  name: string;
  phoneNumber: string;
};

type AgentDeploymentSummary = {
  id: string;
  deployedAt: string;
  status: string;
};

type AgentSessionSummary = {
  id: string;
  createdAt: string;
};

type AgentDashboardData = {
  environments: AgentEnvironmentSummary[];
  latestDeployment: AgentDeploymentSummary | null;
  latestSession: AgentSessionSummary | null;
};

// --- Helpers ---

const formatDuration = (minutes: number): string => {
  if (minutes === 0) return "0m";
  if (minutes < 1) return "< 1m";
  const hours = Math.floor(minutes / 60);
  const remaining = Math.floor(minutes % 60);
  if (hours === 0) return `${remaining}m`;
  if (remaining === 0) return `${hours}h`;
  return `${hours}h ${remaining}m`;
};

const timeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return diffMins <= 1 ? "1m ago" : `${diffMins}m ago`;
  if (diffHours < 24) return diffHours === 1 ? "1h ago" : `${diffHours}h ago`;
  if (diffDays < 30) return diffDays === 1 ? "1d ago" : `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return diffMonths === 1 ? "1mo ago" : `${diffMonths}mo ago`;
};

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";
  // Simple US format: +1XXXXXXXXXX -> (XXX) XXX-XXXX
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

// --- Sub-components ---

type QuickAccessCardProps = {
  title: string;
  description: string;
  icon: string;
  to: string;
  iconColor: string;
};

const QuickAccessCard = ({ title, description, icon, to, iconColor }: QuickAccessCardProps) => {
  const navigate = useNavigate();

  return (
    <button
      className="flex flex-row items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left w-full group"
      onClick={() => navigate(to)}
    >
      <View className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors shrink-0">
        <Icon
          name={icon}
          size={16}
          className={iconColor}
        />
      </View>
      <View className="flex flex-col min-w-0 flex-1">
        <Text className="text-sm font-medium text-gray-900 dark:text-white">{title}</Text>
        <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{description}</Text>
      </View>
      <Icon
        name="chevron-right"
        size={14}
        className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
      />
    </button>
  );
};

type ResourceLinkProps = {
  title: string;
  icon: string;
  to?: string;
  href?: string;
  iconColor: string;
};

const ResourceLink = ({ title, icon, to, href, iconColor }: ResourceLinkProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (href) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else if (to) {
      navigate(to);
    }
  };

  return (
    <button
      onClick={handleClick}
      className="flex flex-row items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left w-full group"
    >
      <Icon
        name={icon}
        size={15}
        className={iconColor}
      />
      <Text className="text-sm text-gray-600 dark:text-gray-300 flex-1">{title}</Text>
      {href && (
        <Icon
          name="open"
          size={12}
          className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      )}
    </button>
  );
};

// --- Getting Started Button (opens global onboarding drawer) ---

const GettingStartedButton = ({ userId }: { userId?: string }) => {
  const { completedCount, totalSteps } = useOnboardingState(userId);

  return (
    <View className="flex flex-col">
      <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3 px-1">Getting started</Text>
      <button
        onClick={() => window.dispatchEvent(new CustomEvent(ONBOARDING_DRAWER_OPEN_EVENT))}
        className="flex flex-row items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-brand/50 hover:bg-brand/5 dark:hover:bg-brand/10 transition-all group"
      >
        <Icon
          name="checklist"
          size={18}
          className="text-brand shrink-0"
        />
        <View className="flex flex-col flex-1 text-left">
          <Text className="text-sm font-medium text-gray-900 dark:text-white">Onboarding checklist</Text>
          <Text className="text-xs text-gray-400 dark:text-gray-500">
            {completedCount}/{totalSteps} steps completed
          </Text>
        </View>
        <Icon
          name="chevron-right"
          size={14}
          className="text-gray-300 dark:text-gray-600 group-hover:text-brand transition-colors shrink-0"
        />
      </button>
    </View>
  );
};

// --- Main Component ---

export const MissionControlPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { organizationId, organization } = useEffectiveOrganization();
  const { open: openCommandPalette } = useCommandPalette();

  // Redux state
  const agentEntities = useAppSelector((state) => state.agent.entities);
  const isLoadingAgents = useAppSelector((state) => state.agent.isLoading);
  const { currentReport, isLoading: isLoadingUsage } = useAppSelector((state) => state.usage);

  // Per-agent dashboard data (environments, deployments, 24h sessions)
  const [agentDataMap, setAgentDataMap] = useState<Record<string, AgentDashboardData>>({});
  const [sessions24hMap, setSessions24hMap] = useState<Record<string, number>>({});

  // Fetch agents on mount
  useEffect(() => {
    dispatch(agentActions.get({}));
  }, [dispatch]);

  useEffect(() => {
    if (organizationId) {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      dispatch(
        getUsageReport({
          startDate: sevenDaysAgo.toISOString().split("T")[0],
          endDate: now.toISOString().split("T")[0],
          organizationId,
        }),
      );
    }
  }, [dispatch, organizationId]);

  // Fetch 24h usage report (separate from 7-day redux report)
  useEffect(() => {
    if (!organizationId) return;
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    makeRequest<{ data: { usage: { agentBreakdown: { agentId: string; sessionCount: number }[] } } }>(
      `/v1/usage/report?startDate=${oneDayAgo.toISOString().split("T")[0]}&endDate=${now.toISOString().split("T")[0]}&organizationId=${organizationId}`,
    )
      .then((res) => {
        const map: Record<string, number> = {};
        res.data?.usage?.agentBreakdown?.forEach((ab) => {
          map[ab.agentId] = ab.sessionCount;
        });
        setSessions24hMap(map);
      })
      .catch(() => {
        // Silently fail - 24h data is supplemental
      });
  }, [organizationId]);

  // Derived data
  const agents = useMemo(() => Object.values(agentEntities), [agentEntities]);

  const agentBreakdown = useMemo(() => {
    return currentReport?.usage?.agentBreakdown || [];
  }, [currentReport]);

  // Merge agents with their session counts and sort by most calls
  const sortedAgents = useMemo(() => {
    const breakdownMap = new Map(agentBreakdown.map((ab) => [ab.agentId, ab]));

    const merged = agents.map((agent) => ({
      ...agent,
      sessionCount: breakdownMap.get(agent.id)?.sessionCount || 0,
      durationMinutes: breakdownMap.get(agent.id)?.durationMinutes || 0,
    }));

    return merged.sort((a, b) => {
      if (b.sessionCount !== a.sessionCount) return b.sessionCount - a.sessionCount;
      const aTime = new Date(a.lastActivityAt || a.updatedAt).getTime();
      const bTime = new Date(b.lastActivityAt || b.updatedAt).getTime();
      return bTime - aTime;
    });
  }, [agents, agentBreakdown]);

  // Fetch environments, latest deployment, and latest session for each agent
  const fetchAgentDetails = useCallback(async (agentId: string) => {
    try {
      const [envRes, deployRes, sessionRes] = await Promise.all([
        makeRequest<{ data: AgentEnvironmentSummary[] }>(`/v1/agents/${agentId}/environments`),
        makeRequest<{ data: AgentDeploymentSummary[] }>(`/v1/agents/${agentId}/deployments?limit=1`),
        makeRequest<{ data: AgentSessionSummary[] }>(`/v1/agents/${agentId}/sessions?limit=1`),
      ]);

      const environments = (envRes.data || []).map((env) => ({
        id: env.id,
        name: env.name,
        phoneNumber: env.phoneNumber,
      }));

      const deployments = deployRes.data || [];
      const latestDeployment = deployments.length > 0 ? deployments[0] : null;

      const sessions = sessionRes.data || [];
      const latestSession = sessions.length > 0 ? { id: sessions[0].id, createdAt: sessions[0].createdAt } : null;

      return { environments, latestDeployment, latestSession };
    } catch {
      return { environments: [], latestDeployment: null, latestSession: null };
    }
  }, []);

  // Only fetch details for the visible agents (top 6) to limit API calls
  const visibleAgents = useMemo(() => sortedAgents.slice(0, 6), [sortedAgents]);

  useEffect(() => {
    if (visibleAgents.length === 0) return;

    const fetchAll = async () => {
      const entries: [string, AgentDashboardData][] = [];
      const results = await Promise.allSettled(
        visibleAgents.map(async (agent) => {
          const data = await fetchAgentDetails(agent.id);
          return { agentId: agent.id, data };
        }),
      );

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          entries.push([result.value.agentId, result.value.data]);
        }
      });

      setAgentDataMap(Object.fromEntries(entries));
    };

    fetchAll();
  }, [visibleAgents, fetchAgentDetails]);

  // Metric values
  const totalSessions = currentReport?.usage?.totalSessions || 0;
  const totalDuration = currentReport?.usage?.totalDurationMinutes || 0;
  const avgSession = totalSessions > 0 ? totalDuration / totalSessions : 0;
  const activeAgentCount = agentBreakdown.filter((a) => a.sessionCount > 0).length;

  const isNewUser = user?.createdAt ? Date.now() - new Date(user.createdAt).getTime() < 24 * 60 * 60 * 1000 : false;

  return (
    <View className="w-full min-h-full bg-white dark:bg-slate-900">
      <View className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <View className="flex flex-col gap-0.5 mb-6">
          <Text className="text-2xl font-bold text-gray-900 dark:text-white">{isNewUser ? "Welcome to VoiceRun" : "Welcome back"}</Text>
        </View>

        {/* Two-column layout */}
        <View className="flex flex-col lg:flex-row gap-6">
          {/* LEFT COLUMN - Stats, Search, Agents */}
          <View className="flex flex-col gap-5 flex-1 min-w-0">
            {/* Stats Row (condensed) */}
            <View className="flex flex-row flex-wrap gap-3">
              {isLoadingUsage ? (
                <>
                  {[...Array(4)].map((_, i) => (
                    <View
                      key={i}
                      className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 animate-pulse"
                    >
                      <View className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
                    </View>
                  ))}
                </>
              ) : (
                <>
                  <Tooltip
                    content="Total number of voice sessions across all agents in the last 7 days"
                    placement="bottom"
                  >
                    <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                      <Icon
                        name="phone"
                        size={14}
                        className="text-blue-500"
                      />
                      <Text className="text-sm font-semibold text-gray-900 dark:text-white">{totalSessions.toLocaleString()}</Text>
                      <Text className="text-xs text-gray-400 dark:text-gray-500">sessions</Text>
                    </View>
                  </Tooltip>
                  <Tooltip
                    content="Combined duration of all voice sessions in the last 7 days"
                    placement="bottom"
                  >
                    <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                      <Icon
                        name="clock"
                        size={14}
                        className="text-emerald-500"
                      />
                      <Text className="text-sm font-semibold text-gray-900 dark:text-white">{formatDuration(totalDuration)}</Text>
                      <Text className="text-xs text-gray-400 dark:text-gray-500">total</Text>
                    </View>
                  </Tooltip>
                  <Tooltip
                    content="Average length of a single session over the last 7 days"
                    placement="bottom"
                  >
                    <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                      <Icon
                        name="trending-up"
                        size={14}
                        className="text-purple-500"
                      />
                      <Text className="text-sm font-semibold text-gray-900 dark:text-white">{formatDuration(avgSession)}</Text>
                      <Text className="text-xs text-gray-400 dark:text-gray-500">avg</Text>
                    </View>
                  </Tooltip>
                  <Tooltip
                    content="Number of agents that had at least one session in the last 7 days"
                    placement="bottom"
                  >
                    <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                      <Icon
                        name="bot"
                        size={14}
                        className="text-amber-500"
                      />
                      <Text className="text-sm font-semibold text-gray-900 dark:text-white">{activeAgentCount}</Text>
                      <Text className="text-xs text-gray-400 dark:text-gray-500">active</Text>
                    </View>
                  </Tooltip>
                </>
              )}
            </View>

            {/* Search Bar */}
            <button
              onClick={openCommandPalette}
              className="flex flex-row items-center gap-2 w-full px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 transition-colors text-left"
            >
              <Icon
                name="search"
                size={16}
                className="text-gray-400 dark:text-gray-500"
              />
              <Text className="text-sm text-gray-400 dark:text-gray-500 flex-1">Search agents, pages, settings...</Text>
              <Text className="text-[11px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">⌘K</Text>
            </button>

            {/* Agents List */}
            <View className="flex flex-col">
              <View className="flex flex-row items-center justify-between mb-3">
                <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Agents</Text>
                <Button
                  variant="ghost"
                  className="!text-xs !text-gray-400 dark:!text-gray-500 !px-2 !py-1 hover:!text-gray-600 dark:hover:!text-gray-300"
                  onClick={() => navigate("/agents")}
                >
                  View All ({agents.length})
                  <Icon
                    name="chevron-right"
                    size={12}
                    className="ml-0.5"
                  />
                </Button>
              </View>

              {isLoadingAgents ? (
                <View className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[...Array(6)].map((_, i) => (
                    <Card
                      key={i}
                      className="p-4 animate-pulse"
                    >
                      <View className="flex flex-row items-center gap-3 mb-3">
                        <View className="h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-lg shrink-0" />
                        <View className="flex flex-col gap-1.5 flex-1">
                          <View className="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                          <View className="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                        </View>
                      </View>
                      <View className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                        <View className="h-2.5 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                        <View className="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                      </View>
                    </Card>
                  ))}
                </View>
              ) : sortedAgents.length > 0 ? (
                <View className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {visibleAgents.map((agent) => {
                    const agentName = agent.name || "Unnamed Agent";
                    const bgColor = getColorForName(agentName);
                    const data = agentDataMap[agent.id];
                    const sessions24h = sessions24hMap[agent.id] || 0;
                    const phoneNumbers = (data?.environments || []).map((env) => env.phoneNumber).filter(Boolean);
                    const envCount = data?.environments?.length || 0;
                    const lastDeployed = data?.latestDeployment?.deployedAt;
                    const latestSession = data?.latestSession;

                    return (
                      <Card
                        key={agent.id}
                        className="p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 group"
                        onClick={() => navigate(`/agents/${agent.id}/overview`)}
                      >
                        {/* Top: Avatar + Name */}
                        <View className="flex flex-row items-center gap-3 mb-3">
                          <View className={`flex items-center justify-center w-9 h-9 rounded-lg ${bgColor} text-white text-xs font-bold shrink-0`}>
                            {agentName.charAt(0).toUpperCase()}
                          </View>
                          <View className="flex flex-col min-w-0 flex-1">
                            <Text className={`text-sm font-medium text-gray-900 dark:text-white truncate ${!agent.name ? "italic" : ""}`}>{agentName}</Text>
                            {agent.sessionCount > 0 && <Text className="text-[11px] text-gray-400 dark:text-gray-500">{agent.sessionCount} sessions / 7d</Text>}
                          </View>
                        </View>

                        {/* Bottom: Detail rows */}
                        <View className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                          {/* 24h sessions */}
                          <View className="flex flex-row items-center gap-1.5">
                            <Icon
                              name="activity"
                              size={12}
                              className="text-blue-400 shrink-0"
                            />
                            <Text className="text-[11px] text-gray-500 dark:text-gray-400">
                              <Text
                                as="span"
                                className="font-medium text-gray-700 dark:text-gray-300"
                              >
                                {sessions24h}
                              </Text>{" "}
                              sessions today
                            </Text>
                          </View>

                          {/* Environments + Phone */}
                          <View className="flex flex-row items-center gap-1.5">
                            <Icon
                              name="layers"
                              size={12}
                              className="text-amber-400 shrink-0"
                            />
                            <Text className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                              {envCount > 0 ? `${envCount} ${envCount === 1 ? "env" : "envs"}` : "No envs"}
                              {phoneNumbers.length > 0 && (
                                <Text
                                  as="span"
                                  className="text-gray-400 dark:text-gray-500"
                                >
                                  {" "}
                                  &middot; {formatPhoneNumber(phoneNumbers[0])}
                                  {phoneNumbers.length > 1 && ` +${phoneNumbers.length - 1}`}
                                </Text>
                              )}
                            </Text>
                          </View>

                          {/* Last deployed */}
                          <View className="flex flex-row items-center gap-1.5">
                            <Icon
                              name="rocket"
                              size={12}
                              className="text-purple-400 shrink-0"
                            />
                            <Text className="text-[11px] text-gray-500 dark:text-gray-400">
                              {lastDeployed ? `Deployed ${timeAgo(lastDeployed)}` : "Not deployed"}
                            </Text>
                          </View>

                          {/* Last session */}
                          {latestSession ? (
                            <button
                              className="flex flex-row items-center gap-1.5 hover:bg-gray-100 dark:hover:bg-gray-700/50 -mx-1 px-1 rounded transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/agents/${agent.id}/sessions?s=${latestSession.id}`);
                              }}
                            >
                              <Icon
                                name="message"
                                size={12}
                                className="text-emerald-400 shrink-0"
                              />
                              <Text className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                                Last session {timeAgo(latestSession.createdAt)}
                              </Text>
                              <Icon
                                name="open"
                                size={9}
                                className="text-gray-300 dark:text-gray-600 ml-auto"
                              />
                            </button>
                          ) : (
                            <View className="flex flex-row items-center gap-1.5">
                              <Icon
                                name="message"
                                size={12}
                                className="text-gray-300 dark:text-gray-600 shrink-0"
                              />
                              <Text className="text-[11px] text-gray-400 dark:text-gray-500">No sessions</Text>
                            </View>
                          )}
                        </View>
                      </Card>
                    );
                  })}
                </View>
              ) : (
                <View className="flex flex-row items-center gap-1.5 py-2">
                  <Text className="text-sm text-gray-400 dark:text-gray-500">No agents yet.</Text>
                  <button
                    onClick={() => navigate("/agents/new")}
                    className="text-sm font-medium text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    Create your first agent
                  </button>
                </View>
              )}
            </View>
          </View>

          {/* RIGHT COLUMN - Onboarding, Quick Access, Resources */}
          <View className="flex flex-col gap-6 w-full lg:w-80 shrink-0">
            {/* Getting Started — opens global onboarding drawer */}
            <GettingStartedButton userId={user?.id} />

            {/* Quick Access */}
            <View>
              <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3 px-1">Quick Access</Text>
              <Card className="p-1.5 overflow-hidden">
                <QuickAccessCard
                  title="Evaluators"
                  description="Score & evaluate sessions"
                  icon="checklist"
                  to="/evaluators"
                  iconColor="text-emerald-500"
                />
                {isAdmin && (
                  <QuickAccessCard
                    title="Simulations"
                    description="Test with simulated callers"
                    icon="play"
                    to="/agents/simulations"
                    iconColor="text-purple-500"
                  />
                )}
                <QuickAccessCard
                  title=" Members"
                  description={organization?.name || "Settings & team"}
                  icon="users"
                  to="/organization/members"
                  iconColor="text-blue-500"
                />
              </Card>
            </View>

            {/* Resources */}
            <View>
              <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3 px-1">Resources</Text>
              <Card className="p-1.5 overflow-hidden">
                <ResourceLink
                  title="TTS Lab"
                  icon="voice-over"
                  to="/voice-lab"
                  iconColor="text-pink-500"
                />
                <ResourceLink
                  title="STT Lab"
                  icon="mic"
                  to="/sttlab"
                  iconColor="text-amber-500"
                />
                <ResourceLink
                  title="Documentation"
                  icon="source"
                  href="https://docs.voicerun.com"
                  iconColor="text-blue-500"
                />
                {isAdmin && (
                  <ResourceLink
                    title="Style Guide"
                    icon="brush"
                    to="/style-guide"
                    iconColor="text-purple-500"
                  />
                )}
              </Card>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};
